
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { approvePremiumSubscription } from "@/services/adminService";
import { Crown, Users, Calendar, UserCheck } from "lucide-react";

const SubscriptionManagement = () => {
  const { toast } = useToast();
  const [userEmail, setUserEmail] = useState("");
  const [duration, setDuration] = useState<'month' | 'year'>('month');
  const [loading, setLoading] = useState(false);
  const [subscribers, setSubscribers] = useState<any[]>([]);

  useEffect(() => {
    fetchSubscribers();
  }, []);

  const fetchSubscribers = async () => {
    try {
      const { data, error } = await supabase
        .from('subscribers')
        .select(`
          *,
          profiles(full_name)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSubscribers(data || []);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const handleApproveSubscription = async () => {
    if (!userEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter user email",
        variant: "destructive"
      });
      return;
    }

    try {
      setLoading(true);

      // Find user by email
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('id')
        .ilike('full_name', `%${userEmail}%`)
        .single();

      if (profileError || !profile) {
        toast({
          title: "Error",
          description: "User not found",
          variant: "destructive"
        });
        return;
      }

      const result = await approvePremiumSubscription(profile.id, duration);

      if (result.success) {
        toast({
          title: "Success",
          description: result.message
        });
        setUserEmail("");
        fetchSubscribers();
      } else {
        toast({
          title: "Error",
          description: result.message,
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error approving subscription:', error);
      toast({
        title: "Error",
        description: "Failed to approve subscription",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            Manual Premium Approval
          </CardTitle>
          <CardDescription>
            Manually approve premium subscriptions for users who have paid
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="User email or name"
              value={userEmail}
              onChange={(e) => setUserEmail(e.target.value)}
            />
            <Select value={duration} onValueChange={(value: 'month' | 'year') => setDuration(value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">1 Month</SelectItem>
                <SelectItem value="year">1 Year</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleApproveSubscription} disabled={loading}>
              <UserCheck className="h-4 w-4 mr-2" />
              {loading ? "Approving..." : "Approve Premium"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Current Subscribers
          </CardTitle>
          <CardDescription>
            Overview of all premium subscribers
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {subscribers.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No subscribers found</p>
            ) : (
              subscribers.map((subscriber) => (
                <div key={subscriber.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    <div>
                      <p className="font-medium">{subscriber.profiles?.full_name || 'Unknown User'}</p>
                      <p className="text-sm text-muted-foreground">{subscriber.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={subscriber.subscribed ? "default" : "secondary"}>
                      {subscriber.subscribed ? "Active" : "Inactive"}
                    </Badge>
                    {subscriber.subscription_end && (
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {new Date(subscriber.subscription_end).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SubscriptionManagement;
